import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useAuth } from '../hooks/useAuth';
import { useSearchScope } from '../hooks/useSearchScope';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { PageContainer } from '../components/PageContainer';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ErrorDisplay } from '../components/ui/ErrorDisplay';
import { ScopeHeader } from '../components/Wines/ScopeHeader';
import { WineryGroupSection, type WineryGroupWine } from '../components/Wines/WineryGroupSection';
import { WineType, formatWineType } from '@wineryxplorer/shared';
import type { DistanceLocale } from '@wineryxplorer/shared';

type WineSearchItem = WineryGroupWine & {
  venue?: { _id: string; name: string; distanceMeters?: number } | null;
};

export const Wines = () => {
  const { selectedRegion, selectedCity, userLocation, mapBounds, locationParam, scopeRestored } =
    useSearchScope();

  const [wineType, setWineType] = useState<WineType | undefined>(undefined);
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(20);

  const debouncedQuery = useDebouncedValue(query.trim(), 250);

  // Reset limit when scope or filters change
  useEffect(() => {
    setLimit(20);
  }, [locationParam?.lat, locationParam?.lng, locationParam?.radius, wineType, debouncedQuery]);

  // Distance unit preference (auth users only; otherwise heuristic from location)
  const { isAuthenticated } = useAuth();
  const { data: profileData } = trpc.user.getMe.useQuery(undefined, {
    enabled: isAuthenticated,
  });
  const distanceLocale: DistanceLocale = useMemo(() => {
    const userDistanceUnit = profileData?.distanceUnit;
    if (userDistanceUnit) return userDistanceUnit === 'km' ? 'metric' : 'imperial';
    if (!userLocation) return 'imperial';
    const { lat, lng } = userLocation;
    const isUS = lat >= 24 && lat <= 50 && lng >= -125 && lng <= -66;
    return isUS ? 'imperial' : 'metric';
  }, [profileData?.distanceUnit, userLocation]);

  // Scope label: mirrors the scope-priority in useSearchScope (bounds > region > city > userLocation)
  const scopeLabel = useMemo<string | null>(() => {
    if (mapBounds) return 'in the area you’re viewing';
    if (selectedRegion) return `in ${selectedRegion.name}`;
    if (selectedCity) return `near ${selectedCity.name}`;
    if (userLocation) return 'near you';
    return null;
  }, [mapBounds, selectedRegion, selectedCity, userLocation]);

  const hasScope = !!locationParam && !!scopeLabel;

  const searchQuery = trpc.wine.search.useQuery(
    {
      q: debouncedQuery.length > 0 ? debouncedQuery : undefined,
      wineType,
      near: locationParam
        ? { lat: locationParam.lat, lng: locationParam.lng, radius: locationParam.radius }
        : undefined,
      limit,
      offset: 0,
    },
    { enabled: scopeRestored && hasScope }
  );

  const items = useMemo(
    () => (searchQuery.data?.items ?? []) as WineSearchItem[],
    [searchQuery.data]
  );
  const total = searchQuery.data?.total ?? 0;
  const hasMore = items.length < total;

  // Group items by venue, preserving server order (already sorted by distance).
  const groups = useMemo(() => {
    const ordered: {
      venue: { _id: string; name: string; distanceMeters?: number };
      wines: WineryGroupWine[];
    }[] = [];
    const indexById = new Map<string, number>();
    for (const item of items) {
      const venueId =
        item.venue?._id ?? (typeof item.venueId === 'string' ? item.venueId : item.venueId?._id);
      if (!venueId) continue;
      const venueName =
        item.venue?.name ?? (typeof item.venueId === 'object' ? (item.venueId?.name ?? '') : '');
      let idx = indexById.get(venueId);
      if (idx === undefined) {
        idx = ordered.length;
        indexById.set(venueId, idx);
        ordered.push({
          venue: { _id: venueId, name: venueName, distanceMeters: item.venue?.distanceMeters },
          wines: [],
        });
      }
      ordered[idx].wines.push(item);
    }
    return ordered;
  }, [items]);

  return (
    <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
      <PageContainer className="space-y-6 mt-2">
        {!scopeRestored ? (
          <div className="py-12 text-center">
            <LoadingSpinner />
          </div>
        ) : !hasScope ? (
          <div>
            <h1 className="font-serif text-3xl text-gray-900 mb-2">Search Wines</h1>
            <p className="text-gray-700 max-w-md">
              Pick a region on the Wineries page to see what&rsquo;s poured there.
            </p>
            <Link
              to="/wineries"
              className="inline-block mt-4 text-primary-700 hover:text-primary-800 font-sans"
            >
              Browse wineries &rarr;
            </Link>
          </div>
        ) : (
          <>
            <ScopeHeader scopeLabel={scopeLabel!} />

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setWineType(undefined)}
                className={`px-3 py-1 text-sm rounded-full transition-colors ${
                  wineType === undefined
                    ? 'bg-primary-700 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All types
              </button>
              {Object.values(WineType).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setWineType(type)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    wineType === type
                      ? 'bg-primary-700 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {formatWineType(type)}
                </button>
              ))}
            </div>

            <div>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Refine — try 'Cabernet Franc'…"
                className="block w-full px-4 py-3 border border-gray-200 rounded-md bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-transparent"
              />
            </div>

            {searchQuery.isPending ? (
              <div className="py-8 text-center">
                <LoadingSpinner />
              </div>
            ) : searchQuery.error ? (
              <ErrorDisplay title="Search Error" message={searchQuery.error.message} />
            ) : items.length === 0 ? (
              <p className="py-8 text-center text-gray-600">
                {debouncedQuery.length > 0 || wineType
                  ? `No matches ${scopeLabel}. Try a different type or term.`
                  : `No wines listed ${scopeLabel} yet.`}
              </p>
            ) : (
              <>
                <div>
                  {groups.map((g) => (
                    <WineryGroupSection
                      key={g.venue._id}
                      winery={g.venue}
                      wines={g.wines}
                      distanceLocale={distanceLocale}
                    />
                  ))}
                </div>
                {hasMore && (
                  <div className="text-center pt-2">
                    <button
                      type="button"
                      onClick={() => setLimit((l) => l + 20)}
                      className="px-4 py-2 text-sm border border-gray-200 rounded-md text-gray-700 hover:bg-gray-100"
                    >
                      Load more ({total - items.length} remaining)
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </PageContainer>
    </div>
  );
};
